/**
 * TeamCockpitPanel — the team-lead daily cockpit (PHOTON-38).
 *
 * Presentational only: takes the `/api/tmd/team-cockpit` payload as `data` and
 * renders the daily view. Layout is a two-column command center:
 *   • LEFT  — a vertical metric rail (leadership KPIs, goal-relative)
 *   • RIGHT — a who-went-quiet strip and the combined priority list.
 *
 * The metric rail is driven by `data.lead_metrics`. That field is not yet
 * produced by the backend, so until it is the rail renders clearly-marked
 * SAMPLE data (see MOCK_METRICS + the "sample" badge). This honors the design
 * language's data-honesty rule: placeholder numbers must never read as real.
 *
 * Data fetching + gating live in TackleMyDayPanel; this component stays pure so
 * it's trivial to wire once the backend contract is confirmed against live data.
 */
import * as React from "react";
import {
  makeStyles,
  tokens,
  shorthands,
  Text,
  Badge,
  Spinner,
  Tooltip,
  ProgressBar,
  Button,
  Textarea,
  FluentProvider,
  webDarkTheme,
} from "@fluentui/react-components";
import { Open16Regular, ArrowLeft16Regular } from "@fluentui/react-icons";
import { TmdBriefingProgress, Phase } from "./TmdBriefingProgress";

// Honest phased-load copy for the cockpit contexts. The shell load is a big
// parallel GHL fetch + in-memory metric tally (no Gemini — the AI briefing
// loads separately in the right column); a rail drill-down is a fast
// deterministic fetch. Neither borrows the My-Day briefing's Gemini/10s copy.
const COCKPIT_LOAD_PHASES: Phase[] = [
  { label: "Fetching the team's pipeline", endAtMs: 1800 },
  { label: "Tallying leadership metrics", endAtMs: 4500 },
  { label: "Finalizing the cockpit", endAtMs: Number.POSITIVE_INFINITY },
];
const DRILL_LOAD_PHASES: Phase[] = [
  { label: "Fetching the records", endAtMs: 1500 },
  { label: "Loading details", endAtMs: Number.POSITIVE_INFINITY },
];

// GHL location base — "Open in CRM" links straight to the opp, matching the
// per-LERT TMD's action row.
const GHL_BASE = "https://crm.coto.services/v2/location/OZ0gNsIxzemC6UfTxrH2";

// ---- Data contract (mirrors tmd_team.build_team_cockpit) -------------------

export interface TeamPulse {
  open_pipeline: number;
  pilot_pipeline: number;
  pilot_count: number;
  at_risk_amount: number;
  wins_mtd: number;
  reps_behind: number;
  deals_slipping: number;
  grant_gated_amount: number;
  grant_gated_count: number;
}

/** Counts of open Main-pipeline deals by how soon they're expected to close. */
export interface CloseWindowCounts {
  d30: number;
  d60: number;
  d90: number;
}

/**
 * Leadership KPIs shown in the left rail. Goal-relative where a goal exists.
 *
 * Wired incrementally: the backend populates only the fields whose data source
 * is live, and every field is optional. Any field the backend omits is rendered
 * by the rail as clearly-marked SAMPLE data (a per-tile "sample" chip + fallback
 * value from SAMPLE_FALLBACK), so a placeholder never reads as a real number.
 *
 * Wired so far: new_opps_mtd (team Main opps past Lead Qualified this month).
 */
export interface LeadMetrics {
  /** Deals won this quarter, against the quarterly close goal. */
  deals_closed_qtr?: number;
  deals_goal_qtr?: number;
  /** Deals won this fiscal/calendar year, against the annual close goal. */
  deals_closed_ytd?: number;
  deals_goal_ytd?: number;
  /** Share of this quarter's check-in calls completed (0–100). */
  checkin_pct_qtr?: number;
  checkin_made_qtr?: number;
  checkin_target_qtr?: number;
  /** Open Main-pipeline deals bucketed by expected-close window. */
  main_pipeline_windows?: CloseWindowCounts;
  /** New opportunities that crossed past Lead Qualified this calendar month. */
  new_opps_mtd?: number;
  /** Pilots currently running (Pilot Start → Conclusion Summary), company-wide. */
  active_pilots?: number;
  /** Pilots that concluded within the last 30 days, company-wide. */
  pilots_closed_30d?: number;
}

/** Backlog counts — computed but NOT surfaced in the daily cockpit; carried
 *  for the future Weekly scorecard so it doesn't recompute them. */
export interface RepBacklog {
  stalled: number;
  slipping: number;
  untouched_30d: number;
}

export interface Escalation {
  lert_name: string;
  user_id: string;
  severity: "high" | "medium" | "low" | string;
  reasons: string[];
  backlog?: RepBacklog;
}

export type TimeSensitiveType =
  | "grant_decision"
  | "pilot_concluded"
  | "checkin_overdue"
  | string;

export interface TimeSensitive {
  type: TimeSensitiveType;
  agency: string;
  owner_lert_name: string;
  detail: string;
}

export interface WentQuiet {
  lert_name: string;
  days_since_active: number;
}

export interface PriorityItem {
  owner_lert_name: string;
  is_self: boolean;
  agency: string;
  pipeline: string;
  stage: string;
  reason: string;
  opp_id: string;
}

// Team TMD (coach) — the cross-rep AI-ranked briefing rendered in the right
// column. A minimal view of the standard BriefingResponse (rollup path); the
// full type lives in TackleMyDayPanel, but the cockpit only reads these fields.
/** A single note on a deal, as attached to each team rec by the backend
 *  (tmd_notes._notes_for_opp). Same shape the per-LERT TMD renders. */
export interface TeamNote {
  body: string;
  date?: string;
  user?: string;
  user_id?: string;
  is_photon?: boolean;
  is_coach?: boolean;
}

export interface TeamBriefingRec {
  opp_id?: string;
  opp_name?: string;
  owner_name?: string;
  contact?: string;
  pipeline?: string;
  stage?: string;
  confidence?: "HIGH" | "MEDIUM" | "LOW" | string;
  confidence_reason?: string;
  headline?: string;
  reasoning?: string;
  days_in_stage?: number;
  /** All notes on the deal within the briefing window — the coach expands a rec
   *  to read these for context before leaving a note. Attached server-side. */
  notes?: TeamNote[];
}

export interface TeamBriefing {
  top_priority?: TeamBriefingRec | null;
  recommendations?: TeamBriefingRec[];
  notes?: string | null;
}

export interface TeamCockpit {
  generated_at: string;
  team_pulse: TeamPulse;
  /** Leadership KPI rail. Optional until the backend produces it. */
  lead_metrics?: LeadMetrics;
  escalations: Escalation[];
  time_sensitive: TimeSensitive[];
  who_went_quiet: WentQuiet[];
  priority_items: PriorityItem[];
}

// ---- styles ----------------------------------------------------------------

// Photon display / body faces (loaded by the portal — see DESIGN-LANGUAGE.md §3).
// Poppins carries the numerals, labels and titles; Open Sans the prose.
const FONT_DISPLAY = "'Poppins', 'Segoe UI Variable Display', 'Segoe UI', system-ui, sans-serif";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", ...shorthands.gap("14px") },

  // Two-column body: metric rail on the left, everything else on the right.
  bodyLayout: {
    display: "flex",
    ...shorthands.gap("16px"),
    alignItems: "flex-start",
    // On narrow widths the rail stacks above the right column.
    "@media (max-width: 860px)": { flexDirection: "column" },
  },
  rail: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("10px"),
    width: "250px",
    flexShrink: 0,
    "@media (max-width: 860px)": { width: "100%" },
  },
  rightCol: {
    flexGrow: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("14px"),
  },

  // --- metric rail ---------------------------------------------------------
  railTile: {
    position: "relative",
    ...shorthands.overflow("hidden"),
    ...shorthands.padding("12px", "14px"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke1),
    boxShadow: tokens.shadow4,
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("8px"),
  },
  // A whole-tile drill-down target (Deals closed, New opps): the tile itself is
  // a <button>, so it hovers and takes focus. Tiles with inner sub-cells
  // (Close window, Pilots) stay plain and let the cells be the buttons instead.
  railTileButton: {
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "inherit",
    width: "100%",
    transition: "background-color 140ms, border-color 140ms",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
    },
  },
  railTileActive: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderColor(tokens.colorBrandStroke1),
  },
  // Severity edge-stripe on a tile carrying a warn/crit state — the gradient-
  // left device from DESIGN-LANGUAGE.md §7.1. Color set inline per severity.
  railStripe: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "4px",
  },
  railValueWarn: { color: tokens.colorPaletteDarkOrangeForeground1 },
  railValueCrit: { color: tokens.colorPaletteRedForeground1 },
  // Decision chip — a computed status verb (On track / Behind pace / Act now),
  // never a color name (DESIGN-LANGUAGE.md §2.5). Leading dot in currentColor.
  decisionChip: {
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.gap("5px"),
    fontFamily: FONT_DISPLAY,
    fontSize: "9.5px",
    fontWeight: 700,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    ...shorthands.padding("3px", "8px"),
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
  },
  decisionDot: { width: "5px", height: "5px", ...shorthands.borderRadius("50%"), backgroundColor: "currentColor" },
  decisionOk: { color: tokens.colorPaletteGreenForeground1, backgroundColor: tokens.colorPaletteGreenBackground1 },
  decisionWarn: { color: tokens.colorPaletteDarkOrangeForeground1, backgroundColor: tokens.colorPaletteDarkOrangeBackground1 },
  decisionCrit: { color: tokens.colorPaletteRedForeground1, backgroundColor: tokens.colorPaletteRedBackground1 },
  railTileHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap("6px"),
  },
  railLabel: {
    fontFamily: FONT_DISPLAY,
    fontSize: "10.5px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "1.1px",
    color: tokens.colorNeutralForeground3,
  },
  railValueRow: { display: "flex", alignItems: "baseline", ...shorthands.gap("6px") },
  railValue: {
    fontFamily: FONT_DISPLAY,
    fontSize: "28px",
    fontWeight: 600,
    lineHeight: "1",
    letterSpacing: "-0.8px",
    color: tokens.colorNeutralForeground1,
    fontVariantNumeric: "tabular-nums",
  },
  railUnit: {
    fontFamily: FONT_DISPLAY,
    fontSize: "14px",
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
  railMeta: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "11.5px",
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
  railPct: {
    fontWeight: 700,
    color: tokens.colorBrandForeground1,
    fontVariantNumeric: "tabular-nums",
  },
  windowsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    ...shorthands.gap("6px"),
    marginTop: "2px",
  },
  windowCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    ...shorthands.gap("2px"),
    ...shorthands.padding("6px", "0"),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    backgroundColor: tokens.colorNeutralBackground2,
    // Button reset — a cell is a real <button> when it drills down.
    ...shorthands.border("1px", "solid", "transparent"),
    fontFamily: "inherit",
    width: "100%",
  },
  windowCellClickable: {
    cursor: "pointer",
    transition: "background-color 140ms, border-color 140ms",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
    },
  },
  windowCellActive: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderColor(tokens.colorBrandStroke1),
  },
  // Drill-down: a clear "Back to team priorities" button above the title so it's
  // always obvious how to return from a close-window list.
  drillBackRow: {
    marginBottom: "10px",
  },
  windowNum: {
    fontFamily: FONT_DISPLAY,
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: "1",
    color: tokens.colorNeutralForeground1,
    fontVariantNumeric: "tabular-nums",
  },
  windowCap: {
    fontSize: "10px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    color: tokens.colorNeutralForeground3,
  },
  // Two-cell layout for the "Pilots" tile (Active | Ended ≤30d), mirroring the
  // close-window grid — each cell is its own drill-down (reuses windowCell*).
  pilotGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    ...shorthands.gap("6px"),
    marginTop: "2px",
  },

  // --- right-column panels -------------------------------------------------
  // Frameless section — header sits directly on the page background with its
  // rows/cards below, matching the Metrics rail (no surrounding panel border).
  frameless: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  panelHead: {
    display: "flex",
    alignItems: "center",
    fontFamily: FONT_DISPLAY,
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: tokens.colorNeutralForeground2,
    marginBottom: "10px",
  },
  headSticker: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "22px",
    height: "22px",
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    fontSize: "13px",
    lineHeight: "1",
    marginRight: "8px",
    flexShrink: 0,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  stickerDanger: { backgroundColor: tokens.colorPaletteRedBackground3 },
  stickerWarning: { backgroundColor: tokens.colorPaletteDarkOrangeBackground3 },
  stickerBrand: { backgroundColor: tokens.colorBrandBackground },
  headInfo: {
    marginLeft: "6px",
    color: tokens.colorNeutralForeground3,
    cursor: "help",
    fontSize: "12px",
    fontWeight: 400,
    ":hover": { color: tokens.colorNeutralForeground1 },
  },
  priList: { display: "flex", flexDirection: "column", ...shorthands.gap("1px") },
  priRow: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("10px"),
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  priAgency: { fontWeight: 600, minWidth: "180px" },
  priReason: { color: tokens.colorNeutralForeground2, fontSize: "12.5px", flexGrow: 1 },

  // --- Team TMD (AI-ranked coach list) ---
  teamRecList: { display: "flex", flexDirection: "column", ...shorthands.gap("8px") },
  teamRec: {
    ...shorthands.padding("10px", "12px"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    cursor: "pointer",
    transition: "background-color 140ms, transform 140ms",
    ":hover": { backgroundColor: tokens.colorNeutralBackground2Hover },
    ":focus-visible": {
      outlineWidth: "2px",
      outlineStyle: "solid",
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: "2px",
    },
  },
  // Top priority gets an accent left edge and a slightly stronger surface.
  teamRecLead: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderLeft("3px", "solid", tokens.colorBrandStroke1),
  },
  // --- check-in-by-rep drill-down -----------------------------------------
  // A rep with zero check-ins this quarter isn't clickable and reads muted.
  checkinRepEmpty: {
    cursor: "default",
    opacity: 0.7,
    ":hover": { backgroundColor: tokens.colorNeutralBackground2 },
  },
  checkinRepTop: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("8px"),
    marginBottom: "6px",
    flexWrap: "wrap",
  },
  checkinRepName: {
    fontWeight: 600,
    fontSize: "13px",
    color: tokens.colorNeutralForeground1,
    flexGrow: 1,
    minWidth: 0,
  },
  checkinRepCount: {
    fontSize: "13px",
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
    fontVariantNumeric: "tabular-nums",
  },
  checkinRepRemaining: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
  checkinRepNone: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  checkinCallList: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("2px"),
    marginTop: "8px",
    ...shorthands.borderTop("1px", "solid", tokens.colorNeutralStroke2),
    paddingTop: "8px",
  },
  checkinCall: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("4px"),
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground3,
    fontSize: "12px",
  },
  checkinCallHead: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("8px"),
  },
  checkinCallDot: {
    width: "7px",
    height: "7px",
    ...shorthands.borderRadius("50%"),
    flexShrink: 0,
  },
  // Contact line under a check-in call: name + phone + email, revealed on expand.
  checkinContact: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    ...shorthands.gap("4px", "14px"),
    paddingLeft: "15px", // align past the status dot
  },
  checkinContactName: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  checkinContactLink: {
    color: tokens.colorBrandForeground1,
    textDecorationLine: "none",
    fontVariantNumeric: "tabular-nums",
    ":hover": { textDecorationLine: "underline" },
  },
  checkinContactMuted: {
    color: tokens.colorNeutralForeground4,
    fontStyle: "italic",
  },
  checkinCallDone: { backgroundColor: tokens.colorPaletteGreenForeground1 },
  checkinCallPending: { backgroundColor: tokens.colorNeutralForeground4 },
  checkinCallAgency: {
    flexGrow: 1,
    minWidth: 0,
    color: tokens.colorNeutralForeground1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  checkinCallDue: {
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
  },
  checkinCallStatusDone: {
    fontSize: "11px",
    fontWeight: 600,
    color: tokens.colorPaletteGreenForeground1,
    flexShrink: 0,
    minWidth: "38px",
    textAlign: "right",
  },
  checkinCallStatusPending: {
    fontSize: "11px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    minWidth: "38px",
    textAlign: "right",
  },
  teamRecTop: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("8px"),
    marginBottom: "4px",
    flexWrap: "wrap",
  },
  teamRecAgency: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    fontSize: "13.5px",
    flexGrow: 1,
    minWidth: 0,
  },
  teamRecHeadline: {
    fontFamily: FONT_DISPLAY,
    color: tokens.colorNeutralForeground1,
    fontSize: "12.5px",
    fontWeight: 600,
    marginBottom: "2px",
  },
  // AI coaching rationale, boxed with a photon-cyan accent stripe so model
  // output is always identifiable as such (DESIGN-LANGUAGE.md §7.6).
  teamRecReason: {
    position: "relative",
    color: tokens.colorNeutralForeground2,
    fontSize: "12px",
    lineHeight: "1.55",
    ...shorthands.padding("9px", "11px", "9px", "13px"),
    marginTop: "8px",
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.overflow("hidden"),
  },
  teamRecReasonStripe: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "3px",
    backgroundImage: `linear-gradient(180deg, ${tokens.colorBrandForeground2}, ${tokens.colorBrandForeground1})`,
  },
  teamRecReasonLead: {
    display: "block",
    fontFamily: FONT_DISPLAY,
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: tokens.colorBrandForeground2,
    marginBottom: "3px",
  },
  teamRecMeta: {
    color: tokens.colorNeutralForeground3,
    fontSize: "11px",
    marginTop: "4px",
    fontVariantNumeric: "tabular-nums",
  },
  teamLeadTag: {
    fontSize: "9.5px",
    fontWeight: 700,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: tokens.colorBrandForeground1,
  },
  teamSkelRow: {
    height: "58px",
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    marginBottom: "8px",
    opacity: 0.6,
  },
  // Coach-note composer (per rec row).
  teamNoteBox: { marginTop: "8px", display: "flex", flexDirection: "column", ...shorthands.gap("6px") },
  teamNoteActions: { display: "flex", alignItems: "center", ...shorthands.gap("8px") },
  teamNoteErr: { fontSize: "11.5px", color: tokens.colorPaletteRedForeground1 },
  // Coach action row — mirrors the per-LERT TMD action row: inline text actions
  // ("+ Coach note", "Open in CRM") side by side beneath the deal's notes.
  coachActions: {
    marginTop: "10px",
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("16px"),
    flexWrap: "wrap",
  },
  coachActionBtn: {
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.gap("4px"),
    background: "none",
    ...shorthands.border("none"),
    padding: "0",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: 600,
    color: tokens.colorBrandForeground1,
    textDecorationLine: "none",
    ":hover": { textDecorationLine: "underline" },
  },
  coachNoteSentInline: {
    fontSize: "11.5px",
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: 600,
  },
  // Notes expander (click a rec to read its deal notes for context).
  teamNotesToggle: {
    background: "none",
    border: "none",
    padding: "0",
    marginTop: "8px",
    cursor: "pointer",
    fontSize: "11.5px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    ":hover": { color: tokens.colorNeutralForeground1 },
  },
  teamNotesBlock: {
    marginTop: "8px",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("8px"),
    ...shorthands.borderTop("1px", "solid", tokens.colorNeutralStroke2),
    paddingTop: "8px",
  },
  teamNote: {
    ...shorthands.padding("6px", "8px"),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderLeft("2px", "solid", tokens.colorNeutralStroke2),
  },
  teamNoteCoach: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderLeft("2px", "solid", tokens.colorBrandForeground1),
  },
  teamNoteMeta: {
    fontSize: "10.5px",
    color: tokens.colorNeutralForeground3,
    marginBottom: "3px",
    fontVariantNumeric: "tabular-nums",
  },
  teamNoteBody: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground1,
    lineHeight: "1.5",
    whiteSpace: "pre-wrap",
  },
  teamNotesNone: { fontSize: "12px", color: tokens.colorNeutralForeground3 },
  muted: { color: tokens.colorNeutralForeground3, fontSize: "12.5px" },
  loading: { display: "flex", justifyContent: "center", ...shorthands.padding("32px") },

  // Dark "command-center" frame — the cockpit is the leadership view, set off
  // from the light rep-facing TMD below it. Rendered inside a nested dark
  // FluentProvider so every token in the sub-components flips coherently.
  provider: {
    backgroundColor: "transparent",
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
  },
  frame: {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke1),
    ...shorthands.overflow("hidden"),
    boxShadow: tokens.shadow16,
  },
  frameBody: { ...shorthands.padding("18px", "20px", "20px") },
  // Brand-gradient header bar — the one sanctioned piece of colorful chrome
  // (DESIGN-LANGUAGE.md §6.8), full-bleed across the top of the dark frame.
  frameHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap("12px"),
    ...shorthands.padding("14px", "20px"),
    backgroundImage: `linear-gradient(120deg, ${tokens.colorBrandBackground2}, ${tokens.colorBrandBackground})`,
    ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke1),
  },
  frameTitleWrap: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("12px"),
  },
  frameTitle: {
    fontFamily: FONT_DISPLAY,
    fontSize: "20px",
    fontWeight: 600,
    letterSpacing: "-0.2px",
    color: "#ffffff",
  },
  frameGlyph: { color: tokens.colorBrandForeground2, marginRight: "7px" },
  // Status chip that sits on the gradient (reps-behind / on-track).
  frameChip: {
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.gap("6px"),
    fontFamily: FONT_DISPLAY,
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    ...shorthands.padding("4px", "10px"),
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    color: "#ffffff",
    backgroundColor: "rgba(255,255,255,0.16)",
  },
  frameChipDot: { width: "6px", height: "6px", ...shorthands.borderRadius("50%") },
  frameStamp: { fontSize: "11px", color: "rgba(255,255,255,0.72)", fontVariantNumeric: "tabular-nums" },
});

/** Fallback values for tiles whose backend data source isn't wired yet. Shown
 *  only alongside a per-tile "sample" chip so they never read as real. */
const SAMPLE_FALLBACK = {
  deals_closed_qtr: 18,
  deals_goal_qtr: 25,
  deals_closed_ytd: 63,
  deals_goal_ytd: 100,
  checkin_pct_qtr: 64,
  checkin_made_qtr: 128,
  checkin_target_qtr: 200,
  main_pipeline_windows: { d30: 6, d60: 11, d90: 19 } as CloseWindowCounts,
  new_opps_mtd: 14,
  active_pilots: 7,
  pilots_closed_30d: 3,
};

const pct = (num: number, den: number) => (den > 0 ? Math.round((num / den) * 100) : 0);

/** A rail tile with an uppercase label and, when its data is still placeholder,
 *  a "sample" chip so a mock value is never mistaken for a real one. */
type Tone = "ok" | "warn" | "crit";

/** Computed status verb for a rail tile, shown as a decision chip. Never a color
 *  name (DESIGN-LANGUAGE.md §2.5). */
const DecisionChip: React.FC<{ label: string; tone: Tone }> = ({ label, tone }) => {
  const s = useStyles();
  const toneClass =
    tone === "ok" ? s.decisionOk : tone === "warn" ? s.decisionWarn : s.decisionCrit;
  return (
    <span className={`${s.decisionChip} ${toneClass}`}>
      <span className={s.decisionDot} aria-hidden />
      {label}
    </span>
  );
};

const RailTile: React.FC<{
  label: string;
  sample: boolean;
  children: React.ReactNode;
  /** When set, the whole tile is a drill-down button. Omit for tiles whose
   *  drill-down lives in inner sub-cells (Close window, Pilots). */
  onClick?: () => void;
  /** This tile's drill-down list is currently open. */
  active?: boolean;
  /** Severity edge-stripe color (warn/crit) for a tile that's off pace. */
  tone?: "warn" | "crit";
  /** Right-of-label slot in the head — the decision chip. */
  headRight?: React.ReactNode;
}> = ({ label, sample, children, onClick, active, tone, headRight }) => {
  const s = useStyles();
  const stripeColor =
    tone === "crit"
      ? tokens.colorPaletteRedForeground1
      : tone === "warn"
      ? tokens.colorPaletteDarkOrangeForeground1
      : undefined;
  const head = (
    <div className={s.railTileHead}>
      <span className={s.railLabel}>{label}</span>
      {sample ? (
        <Badge appearance="tint" color="warning" size="small">sample</Badge>
      ) : (
        headRight
      )}
    </div>
  );
  const stripe = stripeColor && (
    <span
      className={s.railStripe}
      style={{ background: `linear-gradient(180deg, ${stripeColor} 0%, transparent 100%)` }}
      aria-hidden
    />
  );
  if (onClick) {
    return (
      <button
        type="button"
        className={`${s.railTile} ${s.railTileButton} ${active ? s.railTileActive : ""}`}
        aria-pressed={active}
        onClick={onClick}
      >
        {stripe}
        {head}
        {children}
      </button>
    );
  }
  return (
    <div className={s.railTile}>
      {stripe}
      {head}
      {children}
    </div>
  );
};

// ---- sub-components ---------------------------------------------------------

/** Left rail: leadership KPIs stacked vertically, goal-relative where a goal
 *  exists. Each tile renders real data when the backend supplies it, otherwise
 *  a fallback value plus a "sample" chip. Wiring is incremental — see
 *  LeadMetrics. */
/** Which rail tile a drill-down came from. Each maps to a backend list endpoint
 *  and to a stable `key` (drillKey) used to highlight the originating tile. */
export type DrillSpec =
  | { kind: "close-window"; days: 30 | 60 | 90 }
  | { kind: "deals-closed"; period: "quarter" | "year" }
  | { kind: "new-opps" }
  | { kind: "pilots"; state: "active" | "ended" }
  | { kind: "checkins" };

/** Stable identity of a drill-down's source tile — compared against the active
 *  drill to mark the open tile. Also the natural per-kind React key. */
export function drillKey(spec: DrillSpec): string {
  switch (spec.kind) {
    case "close-window":
      return `close-window:${spec.days}`;
    case "deals-closed":
      return `deals-closed:${spec.period}`;
    case "new-opps":
      return "new-opps";
    case "pilots":
      return `pilots:${spec.state}`;
    case "checkins":
      return "checkins";
  }
}

const MetricRail: React.FC<{
  m?: LeadMetrics;
  onOpenDrill?: (spec: DrillSpec) => void;
  onCloseDrill?: () => void;
  activeKey?: string | null;
}> = ({ m, onOpenDrill, onCloseDrill, activeKey }) => {
  const s = useStyles();
  const F = SAMPLE_FALLBACK;

  // Clicking a tile opens its drill-down; clicking the tile whose list is
  // already open toggles back to the priorities list.
  const isActive = (spec: DrillSpec) => activeKey === drillKey(spec);
  const toggle = (spec: DrillSpec) =>
    isActive(spec) ? onCloseDrill?.() : onOpenDrill?.(spec);
  /** onClick for a whole-tile drill, only when the tile has real data. */
  const tileClick = (spec: DrillSpec, real: boolean) =>
    onOpenDrill && real ? () => toggle(spec) : undefined;

  // Per-tile: real when the backend supplied the field(s) this tile needs.
  const closedQtrReal = m?.deals_closed_qtr != null && m?.deals_goal_qtr != null;
  const closedQtr = closedQtrReal ? m!.deals_closed_qtr! : F.deals_closed_qtr;
  const goalQtr = closedQtrReal ? m!.deals_goal_qtr! : F.deals_goal_qtr;

  const closedYtdReal = m?.deals_closed_ytd != null && m?.deals_goal_ytd != null;
  const closedYtd = closedYtdReal ? m!.deals_closed_ytd! : F.deals_closed_ytd;
  const goalYtd = closedYtdReal ? m!.deals_goal_ytd! : F.deals_goal_ytd;

  const checkinReal = m?.checkin_pct_qtr != null;
  const checkinPct = checkinReal ? m!.checkin_pct_qtr! : F.checkin_pct_qtr;
  const checkinMade = checkinReal ? m?.checkin_made_qtr : F.checkin_made_qtr;
  const checkinTarget = checkinReal ? m?.checkin_target_qtr : F.checkin_target_qtr;

  const windowsReal = m?.main_pipeline_windows != null;
  const w = windowsReal ? m!.main_pipeline_windows! : F.main_pipeline_windows;

  const newOppsReal = m?.new_opps_mtd != null;
  const newOpps = newOppsReal ? m!.new_opps_mtd! : F.new_opps_mtd;

  const activePilotsReal = m?.active_pilots != null;
  const activePilots = activePilotsReal ? m!.active_pilots! : F.active_pilots;

  const pilotsClosedReal = m?.pilots_closed_30d != null;
  const pilotsClosed = pilotsClosedReal ? m!.pilots_closed_30d! : F.pilots_closed_30d;

  const qtrPct = pct(closedQtr, goalQtr);
  const ytdPct = pct(closedYtd, goalYtd);

  // Decision chips are computed against PACE, not a flat threshold: a metric is
  // "On track" when its progress keeps up with how much of the period has
  // elapsed. Honest (a goal 40% hit in a 40%-elapsed quarter is on track) and
  // design-language-correct — a verb, not a color name (§2.5, §6.2).
  const now = new Date();
  const yearElapsed =
    (now.getTime() - new Date(now.getFullYear(), 0, 1).getTime()) /
    (new Date(now.getFullYear() + 1, 0, 1).getTime() - new Date(now.getFullYear(), 0, 1).getTime());
  const qMonth = Math.floor(now.getMonth() / 3) * 3;
  const quarterElapsed =
    (now.getTime() - new Date(now.getFullYear(), qMonth, 1).getTime()) /
    (new Date(now.getFullYear(), qMonth + 3, 1).getTime() -
      new Date(now.getFullYear(), qMonth, 1).getTime());
  const paceDecision = (progress: number, elapsed: number): { label: string; tone: Tone } => {
    if (progress >= elapsed - 0.05) return { label: "On track", tone: "ok" };
    if (progress >= elapsed - 0.2) return { label: "Behind pace", tone: "warn" };
    return { label: "Act now", tone: "crit" };
  };
  const qtrDecision = paceDecision(goalQtr ? closedQtr / goalQtr : 0, quarterElapsed);
  const ytdDecision = paceDecision(goalYtd ? closedYtd / goalYtd : 0, yearElapsed);
  const checkinDecision = paceDecision(checkinPct / 100, quarterElapsed);
  const chipTone = (t: Tone): "warn" | "crit" | undefined =>
    t === "warn" ? "warn" : t === "crit" ? "crit" : undefined;
  const valueClass = (real: boolean, t: Tone): string =>
    real && t === "crit" ? s.railValueCrit : real && t === "warn" ? s.railValueWarn : "";

  return (
    <div className={s.rail}>
      {/* Icon + title, matching the "Today's team priorities" panel header. */}
      <PanelHead sticker="📊" tone="brand">Metrics</PanelHead>

      <RailTile
        label="Deals closed · Quarter"
        sample={!closedQtrReal}
        tone={closedQtrReal ? chipTone(qtrDecision.tone) : undefined}
        headRight={closedQtrReal && <DecisionChip label={qtrDecision.label} tone={qtrDecision.tone} />}
        onClick={tileClick({ kind: "deals-closed", period: "quarter" }, closedQtrReal)}
        active={isActive({ kind: "deals-closed", period: "quarter" })}
      >
        <div className={s.railValueRow}>
          <span className={`${s.railValue} ${valueClass(closedQtrReal, qtrDecision.tone)}`}>
            {closedQtr}
          </span>
          <span className={s.railUnit}>/ {goalQtr} goal</span>
        </div>
        <ProgressBar value={Math.min(qtrPct / 100, 1)} thickness="large" />
        <div className={s.railMeta}>
          <span>to goal</span>
          <span className={s.railPct}>{qtrPct}%</span>
        </div>
      </RailTile>

      <RailTile
        label="Deals closed · Year"
        sample={!closedYtdReal}
        tone={closedYtdReal ? chipTone(ytdDecision.tone) : undefined}
        headRight={closedYtdReal && <DecisionChip label={ytdDecision.label} tone={ytdDecision.tone} />}
        onClick={tileClick({ kind: "deals-closed", period: "year" }, closedYtdReal)}
        active={isActive({ kind: "deals-closed", period: "year" })}
      >
        <div className={s.railValueRow}>
          <span className={`${s.railValue} ${valueClass(closedYtdReal, ytdDecision.tone)}`}>
            {closedYtd}
          </span>
          <span className={s.railUnit}>/ {goalYtd} goal</span>
        </div>
        <ProgressBar value={Math.min(ytdPct / 100, 1)} thickness="large" />
        <div className={s.railMeta}>
          <span>to goal</span>
          <span className={s.railPct}>{ytdPct}%</span>
        </div>
      </RailTile>

      <RailTile
        label="Check-in calls · Quarter"
        sample={!checkinReal}
        tone={checkinReal ? chipTone(checkinDecision.tone) : undefined}
        headRight={checkinReal && <DecisionChip label={checkinDecision.label} tone={checkinDecision.tone} />}
        onClick={tileClick({ kind: "checkins" }, checkinReal)}
        active={isActive({ kind: "checkins" })}
      >
        <div className={s.railValueRow}>
          <span className={`${s.railValue} ${valueClass(checkinReal, checkinDecision.tone)}`}>
            {checkinPct}
          </span>
          <span className={s.railUnit}>%</span>
        </div>
        <ProgressBar value={Math.min(checkinPct / 100, 1)} thickness="large" />
        <div className={s.railMeta}>
          <span>calls made</span>
          {checkinMade != null && checkinTarget != null && (
            <span>{checkinMade} / {checkinTarget}</span>
          )}
        </div>
      </RailTile>

      <RailTile label="Main pipeline · Close window" sample={!windowsReal}>
        <div className={s.windowsGrid}>
          {(
            [
              [30, "30d", w.d30],
              [60, "60d", w.d60],
              [90, "90d", w.d90],
            ] as const
          ).map(([days, cap, n]) => {
            const spec: DrillSpec = { kind: "close-window", days };
            const active = isActive(spec);
            // A cell with deals is clickable to drill in; clicking the active
            // cell again returns to the priorities list (toggle).
            const clickable = !!onOpenDrill && windowsReal && n > 0;
            return (
              <button
                key={cap}
                type="button"
                className={`${s.windowCell} ${clickable ? s.windowCellClickable : ""} ${
                  active ? s.windowCellActive : ""
                }`}
                disabled={!clickable && !active}
                aria-pressed={active}
                onClick={() => toggle(spec)}
              >
                <span className={s.windowNum}>{n}</span>
                <span className={s.windowCap}>{cap}</span>
              </button>
            );
          })}
        </div>
      </RailTile>

      <RailTile
        label="New opps · This month"
        sample={!newOppsReal}
        onClick={tileClick({ kind: "new-opps" }, newOppsReal)}
        active={isActive({ kind: "new-opps" })}
      >
        <div className={s.railValueRow}>
          <span className={s.railValue}>{newOpps}</span>
        </div>
      </RailTile>

      {/* Two pilot KPIs on one pane, each an independent drill-down cell — the
          same interaction as the close-window cells. */}
      <RailTile label="Pilots" sample={!activePilotsReal || !pilotsClosedReal}>
        <div className={s.pilotGrid}>
          {(
            [
              ["active", "Active", activePilots, activePilotsReal],
              ["ended", "Ended ≤30d", pilotsClosed, pilotsClosedReal],
            ] as const
          ).map(([state, cap, n, real]) => {
            const spec: DrillSpec = { kind: "pilots", state };
            const active = isActive(spec);
            const clickable = !!onOpenDrill && real && n > 0;
            return (
              <button
                key={state}
                type="button"
                className={`${s.windowCell} ${clickable ? s.windowCellClickable : ""} ${
                  active ? s.windowCellActive : ""
                }`}
                disabled={!clickable && !active}
                aria-pressed={active}
                onClick={() => toggle(spec)}
              >
                <span className={s.windowNum}>{n}</span>
                <span className={s.windowCap}>{cap}</span>
              </button>
            );
          })}
        </div>
      </RailTile>
    </div>
  );
};

/** Panel title with a high-contrast "sticker" chip and an optional ⓘ that
 *  reveals a description tooltip. */
const PanelHead: React.FC<{
  sticker: string;
  tone: "danger" | "warning" | "brand";
  help?: string;
  children: React.ReactNode;
}> = ({ sticker, tone, help, children }) => {
  const s = useStyles();
  const toneClass =
    tone === "danger" ? s.stickerDanger : tone === "warning" ? s.stickerWarning : s.stickerBrand;
  return (
    <div className={s.panelHead}>
      <span className={`${s.headSticker} ${toneClass}`} aria-hidden>
        {sticker}
      </span>
      <span>{children}</span>
      {help && (
        <Tooltip content={help} relationship="description" withArrow>
          <span className={s.headInfo} tabIndex={0} role="button" aria-label="What does this mean?">
            ⓘ
          </span>
        </Tooltip>
      )}
    </div>
  );
};

const PriorityItemsList: React.FC<{
  items: PriorityItem[];
  onOpenOpp?: (oppId: string) => void;
}> = ({ items, onOpenOpp }) => {
  const s = useStyles();
  return (
    <div className={s.frameless}>
      <PanelHead sticker="🎯" tone="brand">Today's priority items</PanelHead>
      <div className={s.priList}>
        {items.map((it) => (
          <div key={it.opp_id} className={s.priRow} onClick={() => onOpenOpp?.(it.opp_id)}>
            <Badge
              appearance={it.is_self ? "filled" : "tint"}
              color={it.is_self ? "brand" : "informative"}
              size="small"
            >
              {it.is_self ? "YOU" : it.owner_lert_name}
            </Badge>
            <span className={s.priAgency}>{it.agency}</span>
            <span className={s.priReason}>
              {it.stage} · {it.reason}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---- Team TMD (AI-ranked coach list) ---------------------------------------

const CONF_COLOR: Record<string, "success" | "warning" | "informative"> = {
  HIGH: "success",
  MEDIUM: "warning",
  LOW: "informative",
};

const TeamRecRow: React.FC<{
  rec: TeamBriefingRec;
  lead: boolean;
  onOpenOpp?: (oppId: string) => void;
  onCoachNote?: (oppId: string, text: string) => Promise<void>;
  /** Draft a starting-point coach note from the deal's status (todo #3). When
   *  provided, a "Suggest" button appears in the composer. */
  onSuggestCoachNote?: (oppId: string) => Promise<string>;
}> = ({ rec, lead, onOpenOpp, onCoachNote, onSuggestCoachNote }) => {
  const s = useStyles();
  const conf = (rec.confidence || "").toUpperCase();
  const [noteOpen, setNoteOpen] = React.useState(false);
  const [noteText, setNoteText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [notesOpen, setNotesOpen] = React.useState(false);
  const [photonOpen, setPhotonOpen] = React.useState(false);
  const [suggesting, setSuggesting] = React.useState(false);

  const allNotes = rec.notes || [];
  const humanNotes = allNotes.filter((n) => !n.is_photon);
  const photonNotes = allNotes.filter((n) => n.is_photon);

  const open = () => rec.opp_id && onOpenOpp?.(rec.opp_id);
  const toggleNotes = () => setNotesOpen((v) => !v);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  const renderNote = (n: TeamNote, key: string) => (
    <div key={key} className={`${s.teamNote} ${n.is_coach ? s.teamNoteCoach : ""}`}>
      <div className={s.teamNoteMeta}>
        {n.date ? new Date(n.date).toLocaleDateString() : ""}
        {n.date && n.user ? " · " : ""}
        {n.user || ""}
        {n.is_coach ? " · Coach note" : ""}
      </div>
      <div className={s.teamNoteBody}>{n.body}</div>
    </div>
  );

  const send = async () => {
    const text = noteText.trim();
    if (!text || !rec.opp_id || !onCoachNote) return;
    setBusy(true);
    setErr(null);
    try {
      await onCoachNote(rec.opp_id, text);
      setSent(true);
      setNoteOpen(false);
      setNoteText("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const suggest = async () => {
    if (!rec.opp_id || !onSuggestCoachNote) return;
    setSuggesting(true);
    setErr(null);
    try {
      const text = (await onSuggestCoachNote(rec.opp_id)).trim();
      if (text) setNoteText(text);
      else setErr("No suggestion came back — write your own note.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div
      className={`${s.teamRec} ${lead ? s.teamRecLead : ""}`}
      role="button"
      tabIndex={0}
      aria-expanded={notesOpen}
      onClick={toggleNotes}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleNotes();
        }
      }}
    >
      <div className={s.teamRecTop}>
        {lead && <span className={s.teamLeadTag}>Top priority</span>}
        <Badge appearance="tint" color="informative" size="small">
          {rec.owner_name || "—"}
        </Badge>
        <span className={s.teamRecAgency}>{rec.opp_name || rec.contact || "(unnamed)"}</span>
        {conf && (
          <Badge appearance="outline" color={CONF_COLOR[conf] ?? "informative"} size="small">
            {conf}
          </Badge>
        )}
      </div>
      {rec.headline && <div className={s.teamRecHeadline}>{rec.headline}</div>}
      {rec.reasoning && (
        <div className={s.teamRecReason}>
          <span className={s.teamRecReasonStripe} aria-hidden />
          <span className={s.teamRecReasonLead}>Coach this rep</span>
          {rec.reasoning}
        </div>
      )}
      <div className={s.teamRecMeta}>
        {[rec.pipeline, rec.stage].filter(Boolean).join(" · ")}
        {conf === "LOW" && rec.confidence_reason ? ` · ${rec.confidence_reason}` : ""}
      </div>

      {/* Notes expander — click the row (or this toggle) to read the deal's
          notes for context, the way the per-LERT TMD does. */}
      <button
        type="button"
        className={s.teamNotesToggle}
        aria-expanded={notesOpen}
        onClick={(e) => {
          stop(e);
          toggleNotes();
        }}
      >
        {notesOpen ? "▾ Hide notes & context" : `▸ Show notes & context (${allNotes.length})`}
      </button>

      {notesOpen && (
        <div className={s.teamNotesBlock} onClick={stop}>
          {humanNotes.length === 0 ? (
            <div className={s.teamNotesNone}>No notes in the deal window.</div>
          ) : (
            humanNotes.map((n, i) => renderNote(n, `h-${i}`))
          )}
          {photonNotes.length > 0 && (
            <>
              <button
                type="button"
                className={s.teamNotesToggle}
                aria-expanded={photonOpen}
                onClick={(e) => {
                  stop(e);
                  setPhotonOpen((v) => !v);
                }}
              >
                {photonOpen
                  ? "▾ Hide Photon activity"
                  : `▸ Photon activity (${photonNotes.length})`}
              </button>
              {photonOpen && photonNotes.map((n, i) => renderNote(n, `p-${i}`))}
            </>
          )}
          {/* Coach action row — the two affordances side by side, matching the
              per-LERT TMD action row (todo: match action row). Lives inside the
              expanded context (todo #4), and the composer replaces the row while
              a note is being written, exactly like the per-LERT TMD. */}
          {onCoachNote && rec.opp_id && noteOpen ? (
            <div className={s.teamNoteBox} onClick={stop}>
              <Textarea
                value={noteText}
                placeholder="Coach note — posts on this deal as you. The rep's Tackle My Day weighs it."
                onChange={(_, d) => setNoteText(d.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    void send();
                  }
                }}
                resize="vertical"
                disabled={busy || suggesting}
              />
              <div className={s.teamNoteActions}>
                <Button
                  size="small"
                  appearance="primary"
                  disabled={busy || suggesting || !noteText.trim()}
                  onClick={(e) => {
                    stop(e);
                    void send();
                  }}
                >
                  {busy ? <Spinner size="tiny" /> : "Add note"}
                </Button>
                {/* AI-suggested starting point (todo #3). */}
                {onSuggestCoachNote && (
                  <Button
                    size="small"
                    appearance="subtle"
                    disabled={busy || suggesting}
                    onClick={(e) => {
                      stop(e);
                      void suggest();
                    }}
                  >
                    {suggesting ? (
                      <Spinner size="tiny" />
                    ) : noteText.trim() ? (
                      "✨ Re-suggest"
                    ) : (
                      "✨ Suggest"
                    )}
                  </Button>
                )}
                <Button
                  size="small"
                  appearance="subtle"
                  disabled={busy || suggesting}
                  onClick={(e) => {
                    stop(e);
                    setNoteOpen(false);
                    setErr(null);
                  }}
                >
                  Cancel
                </Button>
                {err && <span className={s.teamNoteErr}>{err}</span>}
              </div>
            </div>
          ) : (
            (onCoachNote || onOpenOpp) && rec.opp_id && (
              <div className={s.coachActions}>
                {onCoachNote && (
                  <button
                    type="button"
                    className={s.coachActionBtn}
                    onClick={(e) => {
                      stop(e);
                      setNoteOpen(true);
                      setSent(false);
                    }}
                  >
                    {sent ? "+ Add another coach note" : "+ Coach note"}
                  </button>
                )}
                {sent && <span className={s.coachNoteSentInline}>✓ Note added</span>}
                {onOpenOpp && (
                  <button
                    type="button"
                    className={s.coachActionBtn}
                    onClick={(e) => {
                      stop(e);
                      open();
                    }}
                  >
                    <Open16Regular /> Open in CRM
                  </button>
                )}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

/** One check-in call task in the per-rep check-in drill-down. Phone/email are
 *  loaded lazily when the rep is expanded (see CheckinContactInfo). */
export interface CheckinCall {
  agency: string;
  contact?: string;
  contact_id?: string;
  due_date: string; // YYYY-MM-DD
  completed: boolean;
}

/** Lazily-fetched contact details for a check-in call, keyed by contact id. */
export interface CheckinContactInfo {
  phone?: string;
  email?: string;
  name?: string;
}

/** One LERT rep's check-in calls this quarter, for the check-in drill-down. */
export interface CheckinRepGroup {
  owner_name: string;
  made: number;
  target: number;
  pct: number | null; // null = no calls this quarter (not "behind")
  calls: CheckinCall[];
}

/** Drill-down state for a clicked rail tile. Owned by the parent (it does the
 *  fetch); the cockpit renders it in the right column when set. Any rail tile —
 *  close window, deals closed, new opps, pilots, check-ins — produces one. */
export interface DrillState {
  /** drillKey() of the source tile, so the rail can mark the open tile. */
  key: string;
  /** PanelHead emoji for this drill-down. */
  sticker: string;
  label: string;
  count: number;
  loading: boolean;
  recs: TeamBriefingRec[];
  /** Read-only lists (won / concluded deals) hide the coach affordances — there
   *  is nothing to coach on a closed deal — by not wiring the coach handlers. */
  readOnly: boolean;
  /** Message shown when the list is empty (varies by tile). */
  emptyText?: string;
  /** Present only for the "checkins" drill — rendered grouped by rep instead of
   *  as the flat deal-shaped TeamRecRow list. */
  checkinGroups?: CheckinRepGroup[];
}

/** The right column when a rail tile is drilled into: the tile's opps, rendered
 *  with the same TeamRecRow as the coach list (owner badge, notes expander, and
 *  — unless read-only — the coach composer). Deterministic; no AI ranking. */
const DrillDownPanel: React.FC<{
  state: DrillState;
  onBack?: () => void;
  onOpenOpp?: (oppId: string) => void;
  onCoachNote?: (oppId: string, text: string) => Promise<void>;
  onSuggestCoachNote?: (oppId: string) => Promise<string>;
  onFetchCheckinContacts?: (ids: string[]) => Promise<Record<string, CheckinContactInfo>>;
}> = ({ state, onBack, onOpenOpp, onCoachNote, onSuggestCoachNote, onFetchCheckinContacts }) => {
  const s = useStyles();
  // Read-only drills (won / concluded deals) simply don't forward the coach
  // handlers, so TeamRecRow renders without the composer or "Suggest".
  const coachNote = state.readOnly ? undefined : onCoachNote;
  const suggestNote = state.readOnly ? undefined : onSuggestCoachNote;
  return (
    <div className={s.frameless}>
      <div className={s.drillBackRow}>
        <Button
          size="small"
          appearance="secondary"
          icon={<ArrowLeft16Regular />}
          onClick={onBack}
        >
          Back to team priorities
        </Button>
      </div>
      <PanelHead sticker={state.sticker} tone="brand">
        {state.label}
        {!state.loading ? ` (${state.count})` : ""}
      </PanelHead>
      {state.loading ? (
        <TmdBriefingProgress title="Loading…" subtitle="" phases={DRILL_LOAD_PHASES} />
      ) : state.checkinGroups ? (
        // Check-ins drill down grouped BY REP, not as a flat deal list.
        state.checkinGroups.length === 0 ? (
          <Text className={s.muted}>{state.emptyText || "No check-in calls this quarter."}</Text>
        ) : (
          <div className={s.teamRecList}>
            {state.checkinGroups.map((g, i) => (
              <CheckinRepRow
                key={g.owner_name || i}
                group={g}
                onFetchContacts={onFetchCheckinContacts}
              />
            ))}
          </div>
        )
      ) : state.recs.length === 0 ? (
        <Text className={s.muted}>{state.emptyText || "Nothing to show here."}</Text>
      ) : (
        <div className={s.teamRecList}>
          {state.recs.map((r, i) => (
            <TeamRecRow
              key={r.opp_id || i}
              rec={r}
              lead={false}
              onOpenOpp={onOpenOpp}
              onCoachNote={coachNote}
              onSuggestCoachNote={suggestNote}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** One LERT rep in the check-in drill-down: made / target this quarter with a
 *  behind/on-track signal, expandable to that rep's actual check-in calls.
 *  Phone/email are fetched lazily the first time the rep is expanded. */
const CheckinRepRow: React.FC<{
  group: CheckinRepGroup;
  onFetchContacts?: (ids: string[]) => Promise<Record<string, CheckinContactInfo>>;
}> = ({ group, onFetchContacts }) => {
  const s = useStyles();
  const [open, setOpen] = React.useState(false);
  const [contacts, setContacts] = React.useState<Record<string, CheckinContactInfo> | null>(null);
  const [contactsLoading, setContactsLoading] = React.useState(false);
  const { owner_name, made, target, pct, calls } = group;
  const remaining = target - made;
  const none = target === 0;
  // Behind / on-track by completion, mirroring the tile's severity intent.
  const tone =
    none ? "none" : pct != null && pct >= 80 ? "ok" : pct != null && pct >= 50 ? "warn" : "crit";

  // First expand: fetch phone/email for this rep's contacts (once).
  React.useEffect(() => {
    if (!open || contacts || contactsLoading || !onFetchContacts) return;
    const ids = Array.from(
      new Set(calls.map((c) => c.contact_id).filter((x): x is string => !!x))
    );
    if (ids.length === 0) {
      setContacts({});
      return;
    }
    setContactsLoading(true);
    onFetchContacts(ids)
      .then((r) => setContacts(r || {}))
      .catch(() => setContacts({}))
      .finally(() => setContactsLoading(false));
  }, [open, contacts, contactsLoading, onFetchContacts, calls]);

  const toggle = () => !none && setOpen((v) => !v);
  return (
    <div
      className={`${s.teamRec} ${none ? s.checkinRepEmpty : ""}`}
      role={none ? undefined : "button"}
      tabIndex={none ? undefined : 0}
      aria-expanded={none ? undefined : open}
      onClick={toggle}
      onKeyDown={(e) => {
        if (!none && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          toggle();
        }
      }}
    >
      <div className={s.checkinRepTop}>
        <span className={s.checkinRepName}>{owner_name}</span>
        {none ? (
          <span className={s.checkinRepNone}>No check-ins this quarter</span>
        ) : (
          <>
            <span className={s.checkinRepCount}>
              {made} / {target}
            </span>
            <Badge
              appearance="tint"
              color={tone === "ok" ? "success" : tone === "warn" ? "warning" : "danger"}
              size="small"
            >
              {pct}%
            </Badge>
            {remaining > 0 && (
              <span className={s.checkinRepRemaining}>{remaining} to go</span>
            )}
          </>
        )}
      </div>
      {!none && (
        <ProgressBar
          value={target ? made / target : 0}
          thickness="medium"
          color={tone === "ok" ? "success" : tone === "warn" ? "warning" : "error"}
        />
      )}
      {open && !none && (
        <div className={s.checkinCallList} onClick={(e) => e.stopPropagation()}>
          {calls.map((c, i) => {
            const info = (c.contact_id && contacts?.[c.contact_id]) || {};
            const name = info.name || c.contact || "";
            return (
              <div key={`${c.contact_id || c.agency}-${i}`} className={s.checkinCall}>
                <div className={s.checkinCallHead}>
                  <span
                    className={`${s.checkinCallDot} ${
                      c.completed ? s.checkinCallDone : s.checkinCallPending
                    }`}
                    aria-hidden
                  />
                  <span className={s.checkinCallAgency}>{c.agency}</span>
                  <span className={s.checkinCallDue}>{c.due_date}</span>
                  <span
                    className={c.completed ? s.checkinCallStatusDone : s.checkinCallStatusPending}
                  >
                    {c.completed ? "Done" : "To do"}
                  </span>
                </div>
                <div className={s.checkinContact}>
                  {name && <span className={s.checkinContactName}>{name}</span>}
                  {contactsLoading && !contacts ? (
                    <span className={s.checkinContactMuted}>Loading contact…</span>
                  ) : (
                    <>
                      {info.phone ? (
                        <a
                          className={s.checkinContactLink}
                          href={`tel:${info.phone}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {info.phone}
                        </a>
                      ) : (
                        <span className={s.checkinContactMuted}>No phone</span>
                      )}
                      {info.email ? (
                        <a
                          className={s.checkinContactLink}
                          href={`mailto:${info.email}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {info.email}
                        </a>
                      ) : (
                        <span className={s.checkinContactMuted}>No email</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** The cockpit's right-column action list: Photon's cross-rep AI ranking of the
 *  team's most important deals to coach today. Replaces the deterministic
 *  priority list when the team briefing is available. */
const TeamPrioritiesPanel: React.FC<{
  briefing?: TeamBriefing | null;
  loading?: boolean;
  onOpenOpp?: (oppId: string) => void;
  onCoachNote?: (oppId: string, text: string) => Promise<void>;
  onSuggestCoachNote?: (oppId: string) => Promise<string>;
}> = ({ briefing, loading, onOpenOpp, onCoachNote, onSuggestCoachNote }) => {
  const s = useStyles();
  const recs = briefing
    ? ([briefing.top_priority, ...(briefing.recommendations || [])].filter(
        Boolean
      ) as TeamBriefingRec[])
    : [];
  return (
    <div className={s.frameless}>
      <PanelHead
        sticker="🎯"
        tone="brand"
        help="Photon's AI ranks the whole team's open deals and surfaces the ones most worth coaching today. Click a deal to open it in the CRM."
      >
        Today's team priorities
      </PanelHead>
      {loading ? (
        // The real cockpit wait: Photon's Gemini ranking of the team's deals
        // (~10s). The default briefing phases are honest here — it fetches the
        // team's opps, ranks with Gemini, pulls notes/close-date, finalizes.
        <TmdBriefingProgress title="Ranking the team's priorities" />
      ) : recs.length === 0 ? (
        <Text className={s.muted}>
          {briefing?.notes || "No priorities to surface right now."}
        </Text>
      ) : (
        <div className={s.teamRecList}>
          {recs.map((r, i) => (
            <TeamRecRow
              key={r.opp_id || i}
              rec={r}
              lead={i === 0 && !!briefing?.top_priority}
              onOpenOpp={onOpenOpp}
              onCoachNote={onCoachNote}
              onSuggestCoachNote={onSuggestCoachNote}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---- main -------------------------------------------------------------------

export interface TeamCockpitPanelProps {
  data: TeamCockpit | null;
  loading?: boolean;
  onOpenOpp?: (oppId: string) => void;
  /** Team TMD (coach) AI-ranked list for the right column. When present (or
   *  loading), it replaces the deterministic priority_items list. */
  briefing?: TeamBriefing | null;
  briefingLoading?: boolean;
  /** Post a coach note on a team deal (authored as the team lead). Enables the
   *  per-row note composer. */
  onCoachNote?: (oppId: string, text: string) => Promise<void>;
  /** Draft a starting-point coach note for a deal (todo #3). Enables "Suggest". */
  onSuggestCoachNote?: (oppId: string) => Promise<string>;
  /** Rail drill-down: when set, the right column lists the opps behind the
   *  clicked tile instead of the coach list. Parent owns the fetch. */
  drill?: DrillState | null;
  onOpenDrill?: (spec: DrillSpec) => void;
  onCloseDrill?: () => void;
  /** Lazy contact lookup for the check-in drill-down — called when a rep row is
   *  expanded, to fetch that rep's contacts' phone/email. */
  onFetchCheckinContacts?: (ids: string[]) => Promise<Record<string, CheckinContactInfo>>;
}

export const TeamCockpitPanel: React.FC<TeamCockpitPanelProps> = ({
  data,
  loading,
  onOpenOpp,
  briefing,
  briefingLoading,
  onCoachNote,
  onSuggestCoachNote,
  drill,
  onOpenDrill,
  onCloseDrill,
  onFetchCheckinContacts,
}) => {
  const s = useStyles();
  const stamp =
    data && data.generated_at
      ? new Date(data.generated_at).toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })
      : null;
  const body =
    loading || !data ? (
      <TmdBriefingProgress
        title="Loading Team Cockpit"
        subtitle="Gathering the team's numbers…"
        phases={COCKPIT_LOAD_PHASES}
      />
    ) : (
      <div className={s.bodyLayout}>
        <MetricRail
          m={data.lead_metrics}
          onOpenDrill={onOpenDrill}
          onCloseDrill={onCloseDrill}
          activeKey={drill?.key ?? null}
        />
        <div className={s.rightCol}>
          {/* A drilled rail tile takes over the right column; otherwise the
              AI-ranked team TMD list (deterministic list as the fallback). */}
          {drill ? (
            <DrillDownPanel
              state={drill}
              onBack={onCloseDrill}
              onOpenOpp={onOpenOpp}
              onCoachNote={onCoachNote}
              onSuggestCoachNote={onSuggestCoachNote}
              onFetchCheckinContacts={onFetchCheckinContacts}
            />
          ) : briefing || briefingLoading ? (
            <TeamPrioritiesPanel
              briefing={briefing}
              loading={briefingLoading}
              onOpenOpp={onOpenOpp}
              onCoachNote={onCoachNote}
              onSuggestCoachNote={onSuggestCoachNote}
            />
          ) : (
            <PriorityItemsList items={data.priority_items} onOpenOpp={onOpenOpp} />
          )}
        </div>
      </div>
    );
  return (
    <FluentProvider theme={webDarkTheme} className={s.provider}>
      <div className={s.frame}>
        <div className={s.frameHeader}>
          <div className={s.frameTitleWrap}>
            <span className={s.frameTitle}>
              <span className={s.frameGlyph}>◎</span>Team Cockpit
            </span>
            {data && data.team_pulse.reps_behind > 0 && (
              <span className={s.frameChip}>
                <span
                  className={s.frameChipDot}
                  style={{ backgroundColor: tokens.colorPaletteRedForeground1 }}
                />
                Watch · {data.team_pulse.reps_behind} rep
                {data.team_pulse.reps_behind === 1 ? "" : "s"} behind
              </span>
            )}
            {data && data.team_pulse.reps_behind === 0 && (
              <span className={s.frameChip}>
                <span
                  className={s.frameChipDot}
                  style={{ backgroundColor: tokens.colorPaletteGreenForeground1 }}
                />
                Team on track
              </span>
            )}
          </div>
          {stamp && <span className={s.frameStamp}>as of {stamp}</span>}
        </div>
        <div className={s.frameBody}>{body}</div>
      </div>
    </FluentProvider>
  );
};

export default TeamCockpitPanel;
